import { DateTime, Duration } from "@tento-chrono";
import { Column } from "@tento-ui/component-builtin";
import { Storybook } from "@tento-ui/ui/storybook";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { Button } from "@tento-ui/components/button/button";
import { PropertyList } from "@tento-ui/components/input/property-list";
import { Toast } from "@tento-ui/components/toast/toast";
import { Timer } from "@tento-ui/components/timer/timer";
import { PomodoroMiniApp } from "./pomodoro-miniapp";
import { PomodoroTemplate } from "../template";
import { PomodoroTimer } from "../instance";

export class PomodoroMiniAppStoryBook extends Storybook.Default {
  title(): string {
    return "Pomodoro Mini App";
  }

  examples(): Storybook.Example[] {
    return [
      {
        title: "Default",
        render: () =>
          PomodoroMiniApp.make()
            .assignStyles({
              width: "100%",
              maxWidth: "400px",
              height: "400px",
            })
            .bind({
              items: PomodoroTemplate.ALL.map((template) => ({ template })),
            }),
      },
      {
        title: "Zombie Instance Controls",
        render: () => {
          const zombieTimers = new Map<string, Timer>();

          const $container = Column.make();
          $container.assignStyles({
            width: "100%",
            maxWidth: "400px",
            height: "400px",
          });

          const createMiniApp = (zombies: Map<string, Timer>) => {
            const items = PomodoroTemplate.ALL.map((template) => ({
              template,
              zombie: zombies.get(template.identifier),
            }));
            return PomodoroMiniApp.make()
              .assignStyles({
                width: "100%",
                height: "100%",
              })
              .bind({
                items,
                onUpdate: (state) => {
                  Toast.info(`State: ${state.state} (${state.templateId})`);
                },
                shouldNavigateToRunning: zombies.size > 0,
              });
          };

          let $currentMiniApp = createMiniApp(zombieTimers);
          $container.appendChild($currentMiniApp);

          const $pausedZombieBtn = Button.makeWithLight("Rebind with paused", {
            type: "secondary",
          });
          GESTURE_MANAGER.addPointerEvent($pausedZombieBtn, {
            onClick: () => {
              const zombieState: Timer.State.Paused = {
                id: "zombie-paused-1",
                initialDuration: Duration.Time.mins(25),
                startTime: DateTime.utc.now().add({ mins: -15 }),
                state: "paused",
                remainingTime: Duration.Time.mins(10),
              };
              const timer = Timer.fromState(zombieState);
              zombieTimers.clear();
              zombieTimers.set("pomodoro", timer);
              const $oldMiniApp = $currentMiniApp;
              $currentMiniApp = createMiniApp(zombieTimers);
              $container.replaceChild($currentMiniApp, $oldMiniApp);
            },
          });

          const $runningZombieBtn = Button.makeWithLight("Rebind with running", {
            type: "secondary",
          });
          GESTURE_MANAGER.addPointerEvent($runningZombieBtn, {
            onClick: () => {
              const zombieState: Timer.State.Running = {
                id: "zombie-running-1",
                initialDuration: Duration.Time.mins(5),
                startTime: DateTime.utc.now().add({ mins: -4, secs: -30 }),
                state: "running",
                endTime: DateTime.utc.now().add({ secs: 30 }),
              };
              const timer = Timer.fromState(zombieState);
              zombieTimers.clear();
              zombieTimers.set("shortBreak", timer);
              const $oldMiniApp = $currentMiniApp;
              $currentMiniApp = createMiniApp(zombieTimers);
              $container.replaceChild($currentMiniApp, $oldMiniApp);
            },
          });

          const $allZombiesBtn = Button.makeWithLight("Rebind with all", {
            type: "secondary",
          });
          GESTURE_MANAGER.addPointerEvent($allZombiesBtn, {
            onClick: () => {
              zombieTimers.clear();

              const pausedState1: Timer.State.Paused = {
                id: "zombie-all-1",
                initialDuration: Duration.Time.mins(25),
                startTime: DateTime.utc.now().add({ mins: -15 }),
                state: "paused",
                remainingTime: Duration.Time.mins(10),
              };
              zombieTimers.set("pomodoro", Timer.fromState(pausedState1));

              const runningState: Timer.State.Running = {
                id: "zombie-all-2",
                initialDuration: Duration.Time.mins(5),
                startTime: DateTime.utc.now().add({ mins: -4, secs: -30 }),
                state: "running",
                endTime: DateTime.utc.now().add({ secs: 30 }),
              };
              zombieTimers.set("shortBreak", Timer.fromState(runningState));

              const pausedState2: Timer.State.Paused = {
                id: "zombie-all-3",
                initialDuration: Duration.Time.mins(15),
                startTime: DateTime.utc.now().add({ mins: -10 }),
                state: "paused",
                remainingTime: Duration.Time.mins(5),
              };
              zombieTimers.set("longBreak", Timer.fromState(pausedState2));

              const $oldMiniApp = $currentMiniApp;
              $currentMiniApp = createMiniApp(zombieTimers);
              $container.replaceChild($currentMiniApp, $oldMiniApp);
            },
          });

          return {
            $content: $container,
            $controls: PropertyList.makeWith({
              pausedZombie: {
                label: "Paused Zombie",
                $e: $pausedZombieBtn.assignStyles({
                  padding: "4px 12px",
                }),
                order: 0,
              },
              runningZombie: {
                label: "Running Zombie",
                $e: $runningZombieBtn.assignStyles({
                  padding: "4px 12px",
                }),
                order: 1,
              },
              allZombies: {
                label: "All Zombies (1 running)",
                $e: $allZombiesBtn.assignStyles({
                  padding: "4px 12px",
                }),
                order: 2,
              },
            }),
          };
        },
      },
    ];
  }
}
